import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { getStorageRetentionAPI, getStorageUsageAPI, updateRetentionAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import styles from './StorageRetention.module.scss';

const ARCHIVE = ['Auto-archive', 'Manual', 'Never'];
const PURGE = ['Daily', 'Weekly', 'Monthly', 'Manual'];

function validateMatrix(matrix, minimums) {
  const errors = {};
  (matrix || []).forEach((row) => {
    const min = minimums?.[row.key];
    if (min != null && Number(row.retentionDays) < min) {
      errors[row.key] = `Minimum ${min} days for ${row.artifactType}`;
    }
  });
  return errors;
}

export default function StorageRetention() {
  const [matrix, setMatrix] = useState([]);
  const [minimums, setMinimums] = useState({});
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editDays, setEditDays] = useState('');
  const [rowErrors, setRowErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [retRes, useRes] = await Promise.all([getStorageRetentionAPI(), getStorageUsageAPI()]);
      setMatrix(retRes.data?.matrix || []);
      setMinimums(retRes.data?.minimums || {});
      setUsage(useRes.data || null);
      setHasLoaded(true);
      setRowErrors({});
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const chartOptions = useMemo(
    () => ({
      labels: (usage?.byType || []).map((x) => x.type),
      legend: { position: 'bottom' },
      dataLabels: { enabled: true },
    }),
    [usage]
  );

  const chartSeries = useMemo(() => (usage?.byType || []).map((x) => x.gb), [usage]);

  const commitDays = (key) => {
    const n = parseInt(editDays, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Enter a valid number of days');
      return;
    }
    const min = minimums[key];
    if (min != null && n < min) {
      setRowErrors((prev) => ({ ...prev, [key]: `Minimum ${min} days` }));
      toast.error(`Retention must be at least ${min} days`);
      return;
    }
    const next = matrix.map((r) => (r.key === key ? { ...r, retentionDays: n } : r));
    setRowErrors((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    updateRetentionAPI(next)
      .then(() => {
        setMatrix(next);
        setEditingKey(null);
        toast.success('Retention updated');
        load();
      })
      .catch((e) => toast.error(e.message));
  };

  const saveAll = () => {
    const errs = validateMatrix(matrix, minimums);
    setRowErrors(errs);
    if (Object.keys(errs).length) {
      toast.error('Fix validation errors before saving');
      return;
    }
    updateRetentionAPI(matrix)
      .then(() => {
        toast.success('Retention settings saved');
        load();
      })
      .catch((e) => toast.error(e.message));
  };

  const updateRowField = (key, field, value) => {
    setMatrix((m) => m.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Storage & retention"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Settings', path: platformUrl('settings') },
            { label: 'Retention' },
          ]}
        />
        <LoadingSkeleton rows={8} cols={3} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Storage & retention"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Settings', path: platformUrl('settings') },
            { label: 'Retention' },
          ]}
        />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  if (!matrix.length && hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Storage & retention"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Settings', path: platformUrl('settings') },
            { label: 'Retention' },
          ]}
        />
        <EmptyState icon={AlertTriangle} title="No retention rules" description="Nothing configured yet." actionLabel="Reload" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Storage & retention"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Settings', path: platformUrl('settings') },
          { label: 'Retention' },
        ]}
      />

      {error && hasLoaded && (
        <div className="alert alert-warning small mb-2">Last refresh failed: {error.message}</div>
      )}

      <Card className="mb-3">
        <Card.Body>
          <Card.Title as="h2" className="h6">
            Retention matrix
          </Card.Title>
          <Table responsive hover size="sm" className="align-middle mb-0">
            <thead>
              <tr>
                <th>Artifact type</th>
                <th>Retention (days)</th>
                <th>Archive policy</th>
                <th>Purge schedule</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.key}>
                  <td>{row.artifactType}</td>
                  <td>
                    {editingKey === row.key ? (
                      <Form.Control
                        size="sm"
                        type="number"
                        autoFocus
                        value={editDays}
                        onChange={(e) => setEditDays(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitDays(row.key);
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                        style={{ maxWidth: 120 }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-start"
                        onClick={() => {
                          setEditingKey(row.key);
                          setEditDays(String(row.retentionDays));
                        }}
                      >
                        {row.retentionDays}
                      </button>
                    )}
                    {minimums[row.key] != null && (
                      <div className="text-muted small">Min {minimums[row.key]}d</div>
                    )}
                    {rowErrors[row.key] && <div className={styles.errorCell}>{rowErrors[row.key]}</div>}
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={row.archivePolicy}
                      onChange={(e) => updateRowField(row.key, 'archivePolicy', e.target.value)}
                      style={{ minWidth: 140 }}
                    >
                      {ARCHIVE.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={row.purgeSchedule}
                      onChange={(e) => updateRowField(row.key, 'purgeSchedule', e.target.value)}
                      style={{ minWidth: 120 }}
                    >
                      {PURGE.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Badge bg={row.status === 'active' ? 'success' : 'secondary'}>{row.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={12} lg={6}>
          <Card className={styles.chartCard}>
            <Card.Body>
              <Card.Title as="h2" className="h6">
                Storage by artifact type
              </Card.Title>
              {usage?.byType?.length ? (
                <Chart options={chartOptions} series={chartSeries} type="donut" height={280} />
              ) : (
                <p className="text-muted small mb-0">No usage breakdown.</p>
              )}
              {usage?.totals && (
                <p className="small text-muted mb-0 mt-2">
                  Total {usage.totals.usedGb?.toLocaleString()} GB of {usage.totals.limitGb?.toLocaleString()} GB limit
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className={styles.stickySave}>
        <Button variant="primary" onClick={saveAll}>
          Save all
        </Button>
      </div>
    </div>
  );
}
